import Ably from 'ably';

/**
 * Represents a reveal object.
 * @interface Reveal
 * @property {string} packId - The ID of the pack.
 * @property {string} collection - The collection of the reveal.
 * @property {string} templateId - The template ID of the reveal.
 * @property {string} receiver - The receiver of the reveal.
 */
interface Reveal {
  packId: string;
  collection: string;
  templateId: string;
  receiver: string;
}

/**
 * Represents a Dooplicator reveal object.
 * @interface DooplicatorReveal
 * @property {string} collection - The collection of the Dooplicator reveal.
 * @property {number} id - The ID of the Dooplicator reveal.
 */
interface DooplicatorReveal {
  collection: string;
  id: number;
}

/**
 * Represents template IDs.
 * @interface TemplateIds
 * @property {number} templateId - The template ID.
 * @property {string} [collection] - The collection of the template.
 */
export interface TemplateIds {
  templateId: number;
  collection?: string;
}

/**
 * Represents a decoded message.
 * @interface DecodedMessage
 * @property {string} id - The ID of the message.
 * @property {string} name - The name of the message.
 * @property {number} packTypeId - The pack type ID of the message.
 * @property {TemplateIds[]} templateIds - The template IDs of the message.
 * @property {string} [revealName] - The reveal name of the message.
 */
interface DecodedMessage {
  id: string;
  name: string;
  packTypeId: number;
  templateIds: TemplateIds[];
  revealName?: string;
}

/**
 * Encodes a reveal message into a base64 string.
 * @function encodeRevealMessage
 * @param {Object} params - The parameters for encoding the message.
 * @param {Message} params.message - The message to encode.
 * @param {string} [params.type] - The type of the message.
 * @param {string} [params.revealName] - The reveal name of the message.
 * @returns {string} The encoded message as a base64 string.
 */
export const encodeRevealMessage = ({
  message,
  type,
  revealName,
}: {
  message: Ably.Message;
  type?: 'pack';
  revealName?: string;
}): string => {
  const { id, name, data } = message;
  const packTypeId = data.packTypeIds[0]?.id || 0;
  const templateIds =
    type !== 'pack'
      ? message.data.templateIds.map((item: DooplicatorReveal) => ({
          templateId: item.id,
        }))
      : data.transactionOutput.reveals.map((reveal: Reveal) => ({
          templateId: Number(reveal.templateId),
          collection: reveal.collection,
        }));

  const fieldsToEncode = {
    id,
    name,
    packTypeId,
    templateIds,
    revealName,
  };

  const jsonString = JSON.stringify(fieldsToEncode);
  const encodedString = btoa(jsonString);
  return encodedString;
};

/**
 * Decodes a base64 encoded reveal message.
 * @function decodeRevealMessage
 * @param {string} encodedMessage - The encoded message as a base64 string.
 * @returns {DecodedMessage} The decoded message object.
 */
export const decodeRevealMessage = (encodedMessage: string): DecodedMessage => {
  // Decode the base64 string to a JSON string
  const jsonString = atob(encodedMessage);
  // Parse the JSON string back to an object
  const decodedObject = JSON.parse(jsonString) as DecodedMessage;
  return decodedObject;
};
